import React, { useContext, useEffect, useState } from 'react'
import { MainConfig } from '../chapito-static'
import css from './infos-pratiques.css'
import { MainStylesVars, StyleSheet } from '../stylesheet'
import { ResourceImage } from '../components/resource-image'
import { Icon, SocialsList } from '../components/components-lib'
import type { StaticInfoPratiques } from '../types'
import type { InfoPratiquesSection } from '../../../grab/types'
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons'

const CollapsibleSection: React.FC<{ section: InfoPratiquesSection; isLast: boolean }> = ({ section, isLast }) => {
  const config = useContext(MainConfig)
  const [expanded, setExpanded] = useState(section.expand ?? true)
  const hasTitle = !!section.name?.trim()

  const sectionContent = (
    <div style={{ ...styles.sectionContent, backgroundColor: config.colorBlockBackground, color: config.colorBlockText }}>
      {section.description && (
        <div style={styles.description} className={css.description} dangerouslySetInnerHTML={{ __html: section.description }} />
      )}
      {section.buttons.map((button, i) => (
        <a
          key={i}
          href={button.target}
          target="_blank"
          rel="noopener noreferrer"
          style={{ ...styles.actionButton, backgroundColor: config.colorBlockAction, color: '#fff' }}
        >
          {button.icon && <Icon icon={button.icon} style={{ marginRight: 8 }} color="#fff" />}
          {button.name}
        </a>
      ))}
      {section.links && <SocialsList socials={section.links} />}
    </div>
  )

  if (!hasTitle) {
    return <div style={styles.section}>{sectionContent}</div>
  }

  const sectionStyle: React.CSSProperties = {
    ...styles.section,
    ...(!expanded && !isLast ? { borderBottom: '1px solid rgba(0, 0, 0, 0.1)', paddingBottom: 8 } : {}),
  }

  return (
    <div style={sectionStyle}>
      <button onClick={() => setExpanded(!expanded)} style={{ ...styles.sectionHeader, color: config.colorTitle }}>
        <span style={styles.sectionTitle}>{section.name}</span>
        <Icon icon={expanded ? faChevronUp : faChevronDown} style={{ fontSize: 14 }} color={config.colorTitle} />
      </button>
      {expanded && sectionContent}
    </div>
  )
}

const InfoPratiqueCard: React.FC<{ info: StaticInfoPratiques; defaultExpanded?: boolean; hideImages?: boolean; hideTitle?: boolean }> = ({
  info,
  defaultExpanded,
  hideImages,
  hideTitle,
}) => {
  const config = useContext(MainConfig)
  const [expanded, setExpanded] = useState(defaultExpanded ?? false)
  useEffect(() => {
    setExpanded(defaultExpanded ?? false)
  }, [defaultExpanded])
  const sections = [...info.sections].sort((a, b) => b.weight - a.weight)

  const cardBody = (
    <div style={styles.cardBody}>
      {info.image && !hideImages && (
        <div style={styles.imageContainer}>
          <ResourceImage imageId={info.image} alt={info.name} style={{ width: '100%', aspectRatio: '16/9' }} />
        </div>
      )}
      {sections.map((section, i) => (
        <CollapsibleSection key={i} section={section} isLast={i === sections.length - 1} />
      ))}
      {info.copyright && <p style={{ ...styles.copyright, color: config.colorTitle }}>{info.copyright}</p>}
    </div>
  )

  // without a title bar there is no toggle, the content is always visible
  if (hideTitle) {
    return <div style={styles.card}>{cardBody}</div>
  }

  return (
    <div style={styles.card}>
      <button onClick={() => setExpanded(!expanded)} style={{ ...styles.cardHeader, backgroundColor: config.colorBlockBackground }}>
        <div style={styles.cardHeaderLeft}>
          {info.icon && <Icon icon={info.icon} style={{ fontSize: 18, marginRight: 12 }} color={config.colorBlockAction} />}
          <span style={{ ...styles.cardTitle, color: config.colorBlockText }}>{info.name}</span>
        </div>
        <Icon icon={expanded ? faChevronUp : faChevronDown} style={{ fontSize: 14 }} color={config.colorBlockText} />
      </button>
      {expanded && cardBody}
    </div>
  )
}

export const InfosPratiquesList: React.FC<{
  infoPratiques: StaticInfoPratiques[]
  defaultExpanded?: boolean
  hideImages?: boolean
  hideTitle?: boolean
}> = ({ infoPratiques, defaultExpanded, hideImages, hideTitle }) => {
  const sorted = [...infoPratiques].sort((a, b) => b.weight - a.weight)

  return (
    <div style={styles.container}>
      {sorted.map((info) => (
        <InfoPratiqueCard key={info._id} info={info} defaultExpanded={defaultExpanded} hideImages={hideImages} hideTitle={hideTitle} />
      ))}
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: '16px 0',
  },
  card: {
    borderRadius: 12,
    overflow: 'hidden',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    cursor: 'pointer',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    background: 'none',
  },
  cardHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    fontFamily: `var(${MainStylesVars.fontTitle}), sans-serif`,
  },
  cardBody: {
    padding: '0 8px 8px',
  },
  imageContainer: {
    marginBottom: 12,
    borderRadius: 8,
    overflow: 'hidden',
  },
  section: {
    marginBottom: 8,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    cursor: 'pointer',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    background: 'none',
    fontFamily: `var(${MainStylesVars.fontTitle}), sans-serif`,
    fontSize: 15,
    fontWeight: 'bold',
  },
  sectionTitle: {
    flex: 1,
  },
  sectionContent: {
    padding: '12px 16px',
    borderRadius: 8,
  },
  description: {
    fontSize: 14,
    lineHeight: '1.6',
  },
  actionButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px 20px',
    borderRadius: 24,
    textDecoration: 'none',
    fontWeight: 'bold',
    fontSize: 14,
    marginTop: 12,
    marginRight: 8,
  },
  copyright: {
    fontSize: 12,
    fontWeight: '200',
    padding: '8px 16px',
  },
})
